import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import React, { FC, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
    ConfirmModal,
    ControlledCheckbox,
    ControlledInput,
    FormGrid,
    IParams,
    IUserForm,
    Page,
    Section,
    StatusCode,
    Widget,
} from '../../../shared';
import { UserRoles } from '../../components';
import { useUserSchema } from '../../validators';
import { useUser, useSaveUser, useDeleteUser } from '../../hooks/user.hook';

export const UserEditPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<keyof IParams>() as IParams;

    const { data } = useUser(id, { onGetErrorMessage: t('getUserError') });
    const { mutateAsync: deleteUser } = useDeleteUser();

    const onDelete = useCallback(async () => {
        deleteUser({
            id,
            messages: {
                onDeleteSuccessMessage: t('deleteUserSuccess'),
                onDeleteErrorMessage: t('deleteUserError'),
            },
        });
        navigate('/admin/users');
    }, [deleteUser, id, t, navigate]);

    const form = useForm<IUserForm>({
        resolver: yupResolver(useUserSchema()),
        mode: 'onSubmit',
        defaultValues: { enabled: false },
    });

    useEffect(() => {
        if (data) form.reset({ ...data, enabled: data.status === 'enabled' });
    }, [data, form]);

    const { mutateAsync: saveUser } = useSaveUser();

    const onSubmit = async ({ enabled, ...item }: IUserForm) => {
        try {
            const { id } = await saveUser({
                data: {
                    ...item,
                    sub: data ? data.sub : item.email,
                    status: enabled ? 'enabled' : 'pending',
                },
                messages: {
                    onCreateSuccessMessage: t('createUserSuccess'),
                    onCreateErrorMessage: t('createUserError'),
                    onUpdateSuccessMessage: t('updateUserSuccess'),
                    onUpdateErrorMessage: t('updateUserError'),
                },
            });
            if (id) navigate(`/admin/users`);
            navigate(`/admin/users/${id}`);
        } catch (err) {
            if ((err as AxiosError)?.response?.status === StatusCode.CONFLICT) {
                form.setError('email', {
                    message: t('emailUniqErr'),
                });
            } else {
                throw err;
            }
        }
    };

    const actions = [
        <Button onClick={() => navigate('/admin/users')} color="secondary">
            {t('cancel')}
        </Button>,
        <ConfirmModal
            onConfirm={onDelete}
            button={
                <Button color="primary" variant="outlined" data-testid="deleteUser">
                    {t('delete')}
                </Button>
            }
            title={t('userDeleteWarningTitle')}
            text={t('userDeleteWarningText')}
        />,
        <Button type="submit" variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
            {t('save')}
        </Button>,
    ];

    return (
        <Page
            title={id ? `${data?.firstName} ${data?.lastName}` : t('newUser')}
            actions={actions}
            onBack={() => navigate(-1)}
        >
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Widget footer={[...actions].reverse()}>
                        <Stack spacing={2}>
                            <Section title={t('data')}>
                                <FormGrid size={{ xs: 3 }}>
                                    <ControlledInput name="email" label={t('Email')} required />
                                    <ControlledInput name="firstName" label={t('firstNameLabel')} required />
                                    <ControlledInput name="lastName" label={t('lastNameLabel')} required />
                                </FormGrid>
                            </Section>

                            <Section title={t('status')}>
                                <ControlledCheckbox name="enabled" label={t('enabled')} />
                            </Section>

                            {id && (
                                <Section title={t('roles')}>
                                    <UserRoles userId={id} />
                                </Section>
                            )}
                        </Stack>
                    </Widget>
                </form>
            </FormProvider>
        </Page>
    );
};
